import { useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { LocationSearchValue } from '@components/location-search';

import { api } from 'api';

import { LocationList, Translation } from 'api/locations/requests';
import { useStore } from 'models/Provider';

export const useMapToken = () => {
  return useQuery({
    queryKey: ['mapToken'],
    queryFn: api.locations.mapToken,
  });
};

type LanguageError = { language: string; message: string };
type LocationError = AxiosError<{ translations: LanguageError[] }>;

type UpdateProps = {
  externalId: string;
  location: LocationSearchValue | null;
  locationId: number;
  onSuccess?: (locationId: number) => void;
  setLanguageError: (errors: LanguageError[]) => void;
  translations: Translation[];
  isActive?: boolean;
};

export const useUpdate = (props: UpdateProps) => {
  const { externalId, location, locationId, translations, isActive } = props;
  const queryClient = useQueryClient();
  return useMutation(
    () => api.locations.edit(locationId, externalId, translations, location, isActive),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['locations']);
        props.onSuccess?.(locationId);
      },
      onError: (error: LocationError) => {
        const data = error?.response?.data;
        if (data && data.translations) {
          props.setLanguageError(data.translations);
        }
      },
    }
  );
};

type CreateProps = {
  externalId: string;
  location: LocationSearchValue | null;
  onSuccess?: (locationId: number) => void;
  setLanguageError: (errors: LanguageError[]) => void;
  translations: Translation[];
  isActive?: boolean;
};

export const useCreate = (props: CreateProps) => {
  const { externalId, location, translations, isActive } = props;
  const queryClient = useQueryClient();
  return useMutation(() => api.locations.create(externalId, translations, location, isActive), {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['locations']);
      props.onSuccess?.(data.id);
    },
    onError: (error: LocationError) => {
      const data = error?.response?.data;
      if (data && data.translations) {
        props.setLanguageError(data.translations);
      }
    },
  });
};

export const useLocationList = (
  limit: number,
  offset: number,
  search: string,
  setCount: (count: number) => void,
  isActive?: boolean
) => {
  const { current, t } = useStore();
  return useQuery({
    queryKey: ['locations', 'list', limit, offset, search, isActive],
    queryFn: () => api.locations.list(limit, offset, search, isActive),
    onSuccess: (data) => {
      setCount(data.count);
    },
    select: useCallback(
      (data: LocationList) => {
        const results = (data.results ?? []).map((location) => ({
          ...location,
          title:
            location.translations.find(
              (translation) => translation.language === current.info.defaultLanguage
            )?.title ?? t('company.settings.locations.columns.title.notranslation'),
        }));
        return { ...data, results };
      },
      [current.info.defaultLanguage, t]
    ),
  });
};

export const useLocationCounts = (search: string) => {
  return useQuery({
    queryKey: ['locations', 'counts', search],
    queryFn: () => api.locations.counts(search),
  });
};

type DeleteProps = {
  locationId: number;
  onSuccess?: () => void;
};

export const useDelete = (props: DeleteProps) => {
  const queryClient = useQueryClient();
  return useMutation(() => api.locations.deleteLocation(props.locationId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['locations']);
      props.onSuccess?.();
    },
  });
};
